import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import settingsConfig from '../config/settings.config.json';
import { fetchConfig, saveConfig, fetchIntegrationDocument } from './firestoreFunctions';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { getToken } from '../firebase';

const SETTINGS_TYPE_ALIASES = {
  sf: 'salesforce',
  salesforce: 'salesforce',
  maximo_sql: 'maximo',
  microsoft_graph: 'msgraph',
};

const LLM_API_BASE = '/api/llm/v1';

const resolveConfigKey = (pageType) =>
  pageType ? (SETTINGS_TYPE_ALIASES[pageType] ?? pageType) : undefined;

const ChatWindow = ({ isOpen, onClose, pageType, triggerFieldHelp, onFieldHelpSent }) => {
  const [messages, setMessages] = useState([]);
  const [theme] = useState('light');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const [welcomeMessage, setWelcomeMessage] = useState(`Hi! I'm here to help you with your ${pageType} settings. We use these to connect to your system and retreive data. Name one of the feilds and I'll help you fill it out`);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const createNewSession = async () => {
      try {
        console.log('Creating new help session for page type:', pageType);
        const token = await getToken();
        const response = await fetch(`${LLM_API_BASE}/createHelpSession`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            page_type: pageType,
            welcome_message: welcomeMessage
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to create help session: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Help session created with ID:', data.help_session_id);
        setSessionId(data.help_session_id);
        
        // Set initial welcome message
        setMessages([{
          id: '1',
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date()
        }]);
      } catch (error) {
        console.error('Error creating help session:', error);
        setSessionId(null); // Explicitly set to null on error
        setMessages([{
          id: '1',
          role: 'assistant',
          content: "I'm sorry, I encountered an error while starting the help session. Please try closing and reopening the chat window.",
          timestamp: new Date()
        }]);
      }
    };

    if (isOpen && pageType) {
      setSessionId(null); // Reset session ID when opening
      createNewSession();
    } else {
      setSessionId(null); // Reset session ID when closing
      setMessages([]); // Clear messages when closing
    }
  }, [pageType, isOpen]);

  // New useEffect to handle field help messages once session is initialized
  useEffect(() => {
    if (sessionId && window.fieldHelpMessage) {
      // Session is initialized, now we can send the field help message
      sendFieldHelpMessage(window.fieldHelpMessage);
      window.fieldHelpMessage = null;
    }
  }, [sessionId]);

  // New useEffect to handle field help messages when chat is already open
  useEffect(() => {
    if (sessionId && triggerFieldHelp && window.fieldHelpMessage) {
      // Chat is already open and session is ready, send the message
      sendFieldHelpMessage(window.fieldHelpMessage);
      window.fieldHelpMessage = null;
      onFieldHelpSent(); // Reset the trigger
    }
  }, [sessionId, triggerFieldHelp, onFieldHelpSent]);

  if (!isOpen) return null;

  const sendMessageToLLM = async (message) => {
    try {
      if (!sessionId) {
        console.error('No session ID available');
        throw new Error('Chat session not initialized');
      }

      const token = await getToken();
      console.log('Sending message to LLM:', {
        message,
        pageType,
        sessionId,
        messageCount: messages.length
      });

      const response = await fetch(`${LLM_API_BASE}/setupHelp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: message,
          pageType: pageType,
          help_session_id: sessionId,
          conversation: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response from LLM server');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending message to LLM:', error);
      throw error;
    }
  };

  const sendFieldHelpMessage = async (message) => {
    // Add user message to chat
    const newUserMessage = {
      id: String(messages.length + 1),
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const response = await sendMessageToLLM(message);
      
      const assistantMessage = {
        id: String(messages.length + 2),
        role: 'assistant',
        content: response.message,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      // Add error message
      const errorMessage = {
        id: String(messages.length + 2),
        role: 'assistant',
        content: "I'm sorry, I encountered an error while processing your request. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (message, images = [], files = []) => {
    if (!message.trim()) return;
    
    if (!sessionId) {
      const errorMessage = {
        id: String(messages.length + 2),
        role: 'assistant',
        content: "I'm sorry, the chat session hasn't been properly initialized. Please try closing and reopening the chat window.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return false;
    }

    // Add user message
    const newUserMessage = {
      id: String(messages.length + 1),
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const response = await sendMessageToLLM(message);
      
      const assistantMessage = {
        id: String(messages.length + 2),
        role: 'assistant',
        content: response.message,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      // Add error message
      const errorMessage = {
        id: String(messages.length + 2),
        role: 'assistant',
        content: "I'm sorry, I encountered an error while processing your request. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  return (
    <div className="fixed top-0 right-0 h-full w-96 bg-white dark:bg-gray-800 shadow-lg flex flex-col z-40 border-l border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Help Chat</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            theme={theme}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <ChatInput
          onSend={handleSend}
          sendKey="Enter"
          isLoading={isLoading}
          onCancel={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
};

const SettingsPage = ({
  pageType,
  loading,
  selectedWorkgroupId,
  external_save_button=null,
  onConfigSaved = null,
  onConfigSaveError = null
}) => {
  const initialConfigKey = resolveConfigKey(pageType);
  const [page_config, setPageConfig] = useState(
    initialConfigKey ? settingsConfig[initialConfigKey] : null
  );
  const [connection_values, setConnectionValues] = useState({});
  const [individualSettings, setIndividualSettings] = useState({});
  const [maskedStates, setMaskedStates] = useState({});
  const [isChatOpen, setIsChatOpen] = useState(false);
  console.log('SettingsPage', pageType, selectedWorkgroupId);
  const [triggerFieldHelp, setTriggerFieldHelp] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  // Get the configuration for this page type
  useEffect(() => {
    // Helper to generate a user-friendly label from a field name
    const toLabel = (name) => name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

    // Region options for AWS if present in schema
    const awsRegionOptions = [
      { value: 'us-east-1', label: 'US East (N. Virginia) - us-east-1' },
      { value: 'us-east-2', label: 'US East (Ohio) - us-east-2' },
      { value: 'us-west-1', label: 'US West (N. California) - us-west-1' },
      { value: 'us-west-2', label: 'US West (Oregon) - us-west-2' },
      { value: 'ca-central-1', label: 'Canada (Central) - ca-central-1' },
      { value: 'eu-west-1', label: 'EU (Ireland) - eu-west-1' },
      { value: 'eu-central-1', label: 'EU (Frankfurt) - eu-central-1' },
      { value: 'eu-west-2', label: 'EU (London) - eu-west-2' },
      { value: 'eu-west-3', label: 'EU (Paris) - eu-west-3' },
      { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) - ap-northeast-1' },
      { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul) - ap-northeast-2' },
      { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore) - ap-southeast-1' },
      { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney) - ap-southeast-2' },
      { value: 'ap-south-1', label: 'Asia Pacific (Mumbai) - ap-south-1' },
      { value: 'sa-east-1', label: 'South America (São Paulo) - sa-east-1' },
    ];

    let cancelled = false;
    const configKey = resolveConfigKey(pageType);

    setIsConfigLoading(true);
    setConfigError(null);
    setPageConfig(configKey ? settingsConfig[configKey] ?? null : null);
    setMaskedStates({});

    const loadIntegrationSchema = async () => {
      try {
        if (!pageType) {
          if (!cancelled) {
            setPageConfig(null);
            setIsConfigLoading(false);
          }
          return;
        }

        if (!selectedWorkgroupId) {
          if (!cancelled) {
            setIsConfigLoading(false);
          }
          return;
        }

        // Try to fetch integration doc from Firestore: integrations/{pageType}
        const integrationDoc = await fetchIntegrationDocument(pageType, selectedWorkgroupId);
        if (!integrationDoc) {
          if (!configKey || !settingsConfig[configKey]) {
            throw new Error(`No local configuration found for integration "${pageType}".`);
          }
          if (!cancelled) {
            setPageConfig(settingsConfig[configKey]);
            setIsConfigLoading(false);
          }
          return;
        }

        const schema = Array.isArray(integrationDoc.config_schema) ? integrationDoc.config_schema : [];

        // Build fields from config_schema
        const fieldsFromSchema = schema.map((f) => {
          const id = f.name || '';
          const isSecret = !!f.is_secret;
          const base = {
            id,
            label: toLabel(id),
            placeholder: `Enter your ${toLabel(id)}`,
            required: true,
          };

          // Special handling for known types
          if (id === 'region_name') {
            return {
              ...base,
              type: 'select',
              defaultValue: 'us-east-1',
              options: awsRegionOptions,
            };
          }

          if (isSecret) {
            return {
              ...base,
              isSecret: true,
              showPreview: true,
            };
          }

          return base;
        });

        const secretFields = fieldsFromSchema.filter(f => f.isSecret).map(f => f.id);
        const dynamicConfig = {
          title: `${name} Configuration`,
          description: `Configure your ${name} integration settings`,
          fields: fieldsFromSchema,
          secretFields,
        };

        if (!cancelled) {
          setPageConfig(dynamicConfig);
          setIsConfigLoading(false);
        }
      } catch (err) {
        console.error('Failed to load integration schema for', pageType, err);
        if (!cancelled) {
          setPageConfig(null);
          setConfigError(
            'We could not load configuration fields for this integration. Please try again later or contact support.'
          );
          setIsConfigLoading(false);
        }
      }
    };

    loadIntegrationSchema();

    return () => {
      cancelled = true;
    };
  }, [pageType, selectedWorkgroupId]);

  useEffect(() => {
    const fetchConfigData = async () => {
      if (!selectedWorkgroupId || !pageType) {
        console.log('No selected workgroup or page type');
        return;
      }
      const configData = await fetchConfig(selectedWorkgroupId, pageType);
      console.log('Fetched config data:', configData);
      if (configData && typeof configData === 'object' && !Array.isArray(configData)) {
        const { individual_settings, ...restConfig } = configData;
        setConnectionValues(restConfig);
        // Only use individual_settings if it's a plain object (not a corrupted string-turned-object)
        const isValidSettings = individual_settings
          && typeof individual_settings === 'object'
          && !Array.isArray(individual_settings)
          && Object.values(individual_settings).every(v => typeof v === 'string' && ['none', 'allowed', 'required'].includes(v));
        setIndividualSettings(isValidSettings ? individual_settings : {});
      } else {
        setConnectionValues({});
        setIndividualSettings({});
      }
    };
    fetchConfigData();
  }, [pageType, selectedWorkgroupId]);

  useEffect(() => {
    const saveConfigData = async () => {
      if (external_save_button) {
        try {
          const configToSave = { ...connection_values, individual_settings: completeIndividualSettings };
          await saveConfig(selectedWorkgroupId, pageType, configToSave);
          if (onConfigSaved) {
            onConfigSaved(pageType, configToSave);
          }
        } catch (err) {
          console.error(`Error saving ${pageType} configuration:`, err);
          if (onConfigSaveError) {
            onConfigSaveError(err.message || `Failed to save ${pageType} configuration`);
          }
        }
      }
    };
    saveConfigData();
  }, [external_save_button]);

  const onChange = (e) => {
    const { id, value } = e.target;
    setConnectionValues(prev => ({ ...prev, [id]: value }));
  };

  const onToggleMask = (fieldId) => {
    setMaskedStates(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  const handleFieldHelp = (fieldName) => {
    const message = `Help me with the ${fieldName} field`;
    
    // Store the message to send
    window.fieldHelpMessage = message;
    
    // Open chat if not already open
    if (!isChatOpen) {
      setIsChatOpen(true);
    } else {
      // If chat is already open, trigger the message sending
      setTriggerFieldHelp(true);
    }
  };

  const onIndividualSettingChange = (fieldId, value) => {
    setIndividualSettings(prev => ({ ...prev, [fieldId]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const configToSave = { ...connection_values, individual_settings: completeIndividualSettings };
      await saveConfig(selectedWorkgroupId, pageType, configToSave);
      console.log(`${pageType} configuration saved successfully`);
      if (onConfigSaved) {
        onConfigSaved(pageType, configToSave);
      }
    } catch (error) {
      console.error(`Error saving ${pageType} configuration:`, error);
      if (onConfigSaveError) {
        onConfigSaveError(error.message || `Failed to save ${pageType} configuration`);
      }
    }
  };
  
  if (isConfigLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading integration settings…
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-destructive/40 bg-destructive/10 p-6 text-sm text-destructive">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-destructive">
              Unable to load settings
            </h3>
            <p className="mt-1 text-sm text-destructive/80">{configError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!page_config) {
    return null;
  }

  // Build complete individual settings dict with all fields defaulting to 'none'
  const completeIndividualSettings = page_config.fields.reduce((acc, field) => {
    acc[field.id] = individualSettings[field.id] || 'none';
    return acc;
  }, {});

  // Map the fields with dynamic values
  const fields = page_config.fields.map(field => ({
    ...field,
    value: connection_values[field.id],
    onChange: (e) => onChange(e),
    masked: field.isSecret ? maskedStates[field.id] : undefined,
    onToggleMask: field.isSecret ? () => onToggleMask(field.id) : undefined
  }));

  return (
    <div className="relative bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden max-w-3xl mx-auto">
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{page_config.title}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {page_config.description}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsChatOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <span className="mr-1">💬</span>
            Get Help with Settings
          </button>
        </div>
      </div>
      
      <form onSubmit={onSubmit} className="p-5 space-y-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_180px] gap-4 items-end border-b border-gray-200 dark:border-gray-700 pb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Setting
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Individual Setting
            </span>
          </div>
          {fields.map((field) => (
            <div key={field.id} className="grid grid-cols-[1fr_180px] gap-4 items-start">
              {/* Field input column */}
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {field.label}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleFieldHelp(field.label)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    title={`Get help with ${field.label}`}
                  >
                    <span className="mr-1">❓</span>
                    Help
                  </button>
                </div>
                {field.type === 'select' ? (
                  <select
                    id={field.id}
                    name={field.id}
                    value={field.value || field.defaultValue}
                    onChange={field.onChange}
                    disabled={loading}
                    className="mt-1 w-full px-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#ddd', focusRingColor: '#279DD9' }}
                  >
                    {field.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <input
                      type={field.isSecret && field.masked ? "password" : "text"}
                      id={field.id}
                      name={field.id}
                      value={field.value || ''}
                      onChange={field.onChange}
                      disabled={loading}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder={field.placeholder}
                      required={field.required}
                      autoComplete="off"
                      spellCheck="false"
                    />
                    {field.isSecret && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          type="button"
                          onClick={field.onToggleMask}
                          disabled={loading}
                          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {field.masked ? "👁️" : "🔒"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {field.helpText && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {field.helpText}
                  </p>
                )}
                {field.showPreview && field.value && (
                  <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 font-mono">
                    Value Preview: {field.value.slice(0, 4)}...
                    {field.value.length > 8 ? field.value.slice(-4) : ''}
                    <span className="ml-2">
                      (Length: {field.value.length} characters)
                    </span>
                  </div>
                )}
              </div>
              {/* Individual Setting dropdown column */}
              <div className="mt-6">
                <select
                  data-testid={`individual-setting-${field.id}`}
                  value={individualSettings[field.id] || 'none'}
                  onChange={(e) => onIndividualSettingChange(field.id, e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="none">None</option>
                  <option value="allowed">Allowed</option>
                  <option value="required">Required</option>
                </select>
              </div>
            </div>
          ))}
        </div>
        { external_save_button === null &&
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : `Save ${page_config.title}`}
          </button>
        </div>
        }
      </form>
      
      <div className="px-5 py-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="mr-2">🔐</span>
          Your credentials are encrypted and stored securely.
        </div>
      </div>
      <ChatWindow 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)}
        pageType={pageType}
        triggerFieldHelp={triggerFieldHelp}
        onFieldHelpSent={() => setTriggerFieldHelp(false)}
      />
    </div>
  );
};

export default SettingsPage; 
